import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, KeyRound, UserCog, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import EmptyState from "@/components/EmptyState";
import { api, formatApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { useIsMobile } from "@/hooks/useIsMobile";

const roleOptions = [
  ["admin", "Yönetici"],
  ["sales", "Satış Personeli"],
  ["order_manager", "Sipariş Sorumlusu"],
];

export default function UsersPage() {
  const isMobile = useIsMobile();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [openReset, setOpenReset] = useState(null);
  const [newPassword, setNewPassword] = useState("");

  const [newUser, setNewUser] = useState({ full_name: "", email: "", username: "", password: "", role: "sales" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/users");
      setItems(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const createUser = async () => {
    if (isMobile) return;
    if (!newUser.full_name || !newUser.email || !newUser.username || !newUser.password) {
      toast.error("Tüm alanlar zorunludur.");
      return;
    }
    setCreating(true);
    try {
      await api.post("/users", newUser);
      toast.success("Kullanıcı oluşturuldu.");
      setNewUser({ full_name: "", email: "", username: "", password: "", role: "sales" });
      setOpenCreate(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (u) => {
    if (isMobile) return;
    try {
      await api.patch(`/users/${u.id}`, { is_active: !u.is_active });
      toast.success(u.is_active ? "Kullanıcı pasife alındı." : "Kullanıcı aktifleştirildi.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const changeRole = async (u, role) => {
    if (isMobile) return;
    try {
      await api.patch(`/users/${u.id}`, { role });
      toast.success("Rol güncellendi.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const resetPassword = async () => {
    if (isMobile) return;
    if (!newPassword || newPassword.length < 6) { toast.error("Parola en az 6 karakter olmalıdır."); return; }
    try {
      await api.post(`/users/${openReset}/reset-password`, { new_password: newPassword });
      toast.success("Parola sıfırlandı.");
      setOpenReset(null);
      setNewPassword("");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleDeleteUser = async (u) => {
    if (isMobile) return;
    if (!window.confirm("Bu kullanıcıyı KALICI OLARAK silmek istediğinize emin misiniz? Bu işlem geri alınamaz!")) return;
    const confirmText = window.prompt("Lütfen silme işlemini onaylamak için büyük harflerle 'SİL' yazın:");
    if (confirmText !== "SİL") {
      toast.error("Silme işlemi onaylanmadı.");
      return;
    }
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Kullanıcı kalıcı olarak silindi.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 bg-[#F4F6F8] min-h-full" data-testid="users-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#171A1F] tracking-tight">Kullanıcılar</h1>
          <p className="text-xs text-slate-500 mt-1">Sistem kullanıcıları ve rolleri.</p>
        </div>
        {!isMobile && (
          <Dialog open={openCreate} onOpenChange={setOpenCreate}>
            <DialogTrigger asChild>
              <Button className="bg-[#FF353A] hover:bg-[#E02B30] text-white shadow-sm font-medium" data-testid="users-create-button">
                <Plus size={16} className="mr-2" /> Yeni Kullanıcı
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-[#DCE2E8]">
              <DialogHeader>
                <DialogTitle className="text-[#171A1F]">Yeni Kullanıcı</DialogTitle>
              </DialogHeader>
              <div className="grid gap-3">
                <div><Label className="text-xs font-semibold text-slate-700">Ad Soyad</Label><Input value={newUser.full_name} onChange={(e) => setNewUser((u) => ({ ...u, full_name: e.target.value }))} className="border-[#DCE2E8]" data-testid="new-user-fullname" /></div>
                <div><Label className="text-xs font-semibold text-slate-700">Kullanıcı Adı</Label><Input value={newUser.username} onChange={(e) => setNewUser((u) => ({ ...u, username: e.target.value }))} className="border-[#DCE2E8]" data-testid="new-user-username" /></div>
                <div><Label className="text-xs font-semibold text-slate-700">E-posta</Label><Input type="email" value={newUser.email} onChange={(e) => setNewUser((u) => ({ ...u, email: e.target.value }))} className="border-[#DCE2E8]" data-testid="new-user-email" /></div>
                <div><Label className="text-xs font-semibold text-slate-700">Parola</Label><Input type="password" value={newUser.password} onChange={(e) => setNewUser((u) => ({ ...u, password: e.target.value }))} className="border-[#DCE2E8]" data-testid="new-user-password" /></div>
                <div>
                  <Label className="text-xs font-semibold text-slate-700">Rol</Label>
                  <Select value={newUser.role} onValueChange={(v) => setNewUser((u) => ({ ...u, role: v }))}>
                    <SelectTrigger className="border-[#DCE2E8]" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-[#DCE2E8]">
                      {roleOptions.map(([k, l]) => (<SelectItem key={k} value={k}>{l}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={createUser} disabled={creating} className="bg-[#FF353A] hover:bg-[#E02B30] text-white shadow-sm font-medium" data-testid="new-user-submit">
                  {creating ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
                  Oluştur
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="rounded-lg border border-[#DCE2E8] bg-white shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-slate-500"><Loader2 className="animate-spin text-[#FF353A]" size={16} /> Yükleniyor...</div>
        ) : items.length === 0 ? (
          <EmptyState title="Kullanıcı yok" description="Yeni kullanıcı oluşturun." />
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="block md:hidden divide-y divide-[#DCE2E8]">
              {items.map((u) => (
                <div key={u.id} className="p-4 space-y-3" data-testid={`user-card-${u.username}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="text-sm font-semibold text-[#171A1F]">{u.full_name}</div>
                      <div className="font-mono text-xs font-bold text-[#FF353A]">{u.username}</div>
                    </div>
                    {u.is_active ? <StatusBadge tone="success">Aktif</StatusBadge> : <StatusBadge tone="danger">Pasif</StatusBadge>}
                  </div>

                  <div className="text-xs text-slate-600">{u.email}</div>

                  <div className="flex items-center justify-between gap-2 pt-1 border-t border-slate-100">
                    <span className="text-xs font-medium text-slate-500">Rol:</span>
                    <span className="text-xs font-semibold text-slate-700">
                      {roleOptions.find(([k]) => k === u.role)?.[1] || u.role}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <Table className="hidden md:table">
              <TableHeader>
                <TableRow className="bg-[#F4F6F8] hover:bg-[#F4F6F8]">
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Ad Soyad</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Kullanıcı Adı</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">E-posta</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Rol</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Durum</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Son Giriş</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 text-right">İşlem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((u) => (
                  <TableRow key={u.id} className="border-b border-[#DCE2E8]" data-testid={`user-row-${u.username}`}>
                    <TableCell className="text-sm font-medium text-[#171A1F]">{u.full_name}</TableCell>
                    <TableCell className="font-mono text-sm font-semibold text-[#FF353A]">{u.username}</TableCell>
                    <TableCell className="text-sm text-slate-700">{u.email}</TableCell>
                    <TableCell>
                      <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                        <SelectTrigger className="w-40 border-[#DCE2E8]" data-testid={`user-role-${u.username}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white border-[#DCE2E8]">
                          {roleOptions.map(([k, l]) => (<SelectItem key={k} value={k}>{l}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      {u.is_active ? <StatusBadge tone="success">Aktif</StatusBadge> : <StatusBadge tone="danger">Pasif</StatusBadge>}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">{u.last_login_at ? formatDateTime(u.last_login_at) : "—"}</TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => toggleActive(u)} data-testid={`user-toggle-active-${u.username}`}>
                        <UserCog size={14} className="mr-1" /> {u.is_active ? "Pasifleştir" : "Aktifleştir"}
                      </Button>
                      <Dialog open={openReset === u.id} onOpenChange={(o) => { setOpenReset(o ? u.id : null); if (!o) setNewPassword(""); }}>
                        <DialogTrigger asChild>
                          <Button size="sm" variant="ghost" data-testid={`user-reset-password-${u.username}`}>
                            <KeyRound size={14} className="mr-1" /> Parola Sıfırla
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-white border-[#DCE2E8]">
                          <DialogHeader>
                            <DialogTitle className="text-[#171A1F]">Parola Sıfırla — {u.full_name}</DialogTitle>
                          </DialogHeader>
                          <div>
                            <Label className="text-xs font-semibold text-slate-700">Yeni Parola</Label>
                            <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="border-[#DCE2E8]" data-testid="reset-password-input" />
                          </div>
                          <DialogFooter>
                            <Button onClick={resetPassword} className="bg-[#FF353A] hover:bg-[#E02B30] text-white shadow-sm font-medium" data-testid="reset-password-submit">Sıfırla</Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteUser(u)}
                        className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                        data-testid={`user-delete-${u.username}`}
                      >
                        <Trash2 size={14} className="mr-1" /> Sil
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </div>
    </div>
  );
}
